import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import Update from 'immutability-helper';
import WorkAreaInput from '../../containers/Engineering/WorkAreaInput';
import WorkAreaButtons from './WorkAreaButtons';
import WorkAreaRadioButtonGroup from './WorkAreaRadioButtonGroup';
import WorkAreaOutput from './WorkAreaOutput';
import { Loading } from '../Loading/Loading';

import './WorkArea.sass';

export default class WorkArea extends Component {
  constructor(props) {
    super(props);

    this.state = {
      detached: false,
      valid: true,
      data: props.inputs,
      isDirty: false,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // if the inputs haven't changed, don't sync the state or we'll lose our unsaved changes
    if (prevProps.inputs !== this.props.inputs) {
      this.setState({
        data: this.props.inputs,
        isDirty: false,
      });
    }
  }

  getFormData = () => this.state.data;

  changeValue = (name, value) => {
    const data = Update(this.state.data, {
      [name]: { $set: value },
    });
    this.setState({
      isDirty: true,
      data,
    });
  };

  submit = () => {
    // get a list of actual "inputs" that need to be sent to the server
    const fields = this.props.sections.reduce((prev, item) => {
      if (item.inputs && item.inputs.length) {
        return prev.concat(item.inputs.map((i) => i.name));
      } else {
        return prev;
      }
    }, []);

    // now pluck these from the state
    let data = {};
    for (let i = 0; i < fields.length; i++) {
      let field = fields[i];
      data[field] = this.state.data[field];
    }

    this.props.submit(this.props.node, data);
  };

  toggleDetached = () => {
    this.setState({
      detached: !this.state.detached,
    });
  };

  render() {
    const sections = this.props.sections.map((section, i) => (
      <Section
        key={i}
        node={this.props.node}
        title={section.title}
        columns={section.columns || []}
        buttons={section.buttons || []}
        inputs={section.inputs || []}
        outputs={section.outputs || []}
        data={this.state.data || {}}
        alldata={this.props.alldata}
        changeValue={this.changeValue}
        disabled={this.props.loading}
      />
    ));

    let detachIcon = this.state.detached
      ? 'fa fa-toggle-right'
      : 'fa fa-external-link-square';
    let detachButton = (
      <button
        className="btn btn-xs float-end"
        title="Detach"
        onClick={this.toggleDetached}
      >
        <i className={detachIcon} />
      </button>
    );

    let classes = 'tool-window-dialog';
    if (this.state.detached) {
      classes += ' detached';
    }
    if (!this.state.valid) {
      classes += ' error';
    }
    if (this.state.isDirty) {
      classes += ' dirty';
    }

    let formClasses = 'form form-horizontal';
    if (this.props.loading) {
      formClasses += ' disabled';
    }

    return (
      <div className={classes} role="region" aria-label={`${this.props.title} work area`}>
        <div className="tool-window-title">
          {this.props.title}
          {this.props.detachable ? detachButton : null}
        </div>
        <div className="tool-window-body">
          {this.props.loading || this.props.anyLoading ? (
            <Loading showIcon={this.props.loading} />
          ) : null}
          <form ref={(ref) => ref === 'form'} className={formClasses}>
            {sections}
          </form>
        </div>
        <WorkAreaButtons
          node={this.props.node}
          title={this.props.title}
          disabled={this.props.loading}
          readOnly={this.props.readOnly}
          isDirty={this.state.isDirty}
          validate={() => this.validateForm()}
          getData={() => this.getFormData()}
          submit={this.submit}
          reset={this.props.reset}
          close={this.props.close}
        />
      </div>
    );
  }
}
WorkArea.propTypes = {
  node: PropTypes.string,
  title: PropTypes.string,
  sections: PropTypes.array,
  readOnly: PropTypes.bool,
  order: PropTypes.number,
  loading: PropTypes.bool,
  anyLoading: PropTypes.bool,
  detachable: PropTypes.bool,
  inputs: PropTypes.object,
};
WorkArea.defaultProps = {
  node: '',
  title: '',
  sections: [],
  readOnly: false,
  order: 99,
  loading: false,
  anyLoading: false,
  detachable: false,
  inputs: {},
};

class Section extends Component {
  /**
   * Gets the dependent value for the showIf/disableIf condition
   * @param input
   * @returns {null}
   */
  getDepValue = (input) => {
    let value;
    if (input.hasOwnProperty('showIf')) {
      value = this.props.alldata[input.showIf.input] || null;
    } else if (input.hasOwnProperty('disableIf')) {
      value = this.props.alldata[input.disableIf.input] || null;
    }
    return value;
  };

  render() {
    let columns = null,
      title = null;

    if (this.props.title.length) {
      title = <legend>{this.props.title}</legend>;
    }

    if (this.props.columns.length) {
      columns = (
        <div className="form-group">
          {this.props.columns.map((column) => {
            let align = column.align || 'left';
            return (
              <div key={column.title} className={'col-xs-6 text-' + align}>
                <h5>{column.title}</h5>
              </div>
            );
          })}
        </div>
      );
    }
    const inputs = this.props.inputs.map((input) => {
      let value = this.props.data.hasOwnProperty(input.name)
        ? this.props.data[input.name]
        : '';
      if (input.type === 'buttonGroup') {
        return (
          <WorkAreaRadioButtonGroup
            key={'rbg-' + input.label}
            node={this.props.node}
            values={this.props.data}
            label={input.label}
            buttons={input.buttons}
            orientation={input.orientation}
          />
        );
      } else {
        return (
          <WorkAreaInput
            key={input.name}
            node={this.props.node}
            value={value}
            depValue={this.getDepValue(input)}
            changeValue={this.props.changeValue}
            {...input}
          />
        );
      }
    });

    const outputs = this.props.outputs.map((output) => {
      let value = this.props.data.hasOwnProperty(output.name)
        ? this.props.data[output.name]
        : '';
      return (
        <WorkAreaOutput
          key={output.name}
          label={output.label}
          labelHTML={output.labelHTML || false}
          units={output.units}
          value={value}
          depValue={this.getDepValue(output)}
          {...output}
        />
      );
    });

    const content = (
      <>
        {title}
        {columns}
        {inputs}
        {outputs}
      </>
    );

    // Only use fieldset if there's a title (legend)
    if (this.props.title.length) {
      return <fieldset>{content}</fieldset>;
    } else {
      return <div>{content}</div>;
    }
  }
}
Section.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.array,
  inputs: PropTypes.array,
  outputs: PropTypes.array,
  buttons: PropTypes.array,
  node: PropTypes.string.isRequired,
  data: PropTypes.object,
};
Section.defaultProps = {
  title: '',
  columns: [],
  inputs: [],
  outputs: [],
  buttons: [],
  node: '',
  data: {},
};
